import React, { useEffect, useRef, useState } from "react";
import { SaveStatus } from "../../../shared/utility/save-status";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import KuikaButton, { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaModalIconWrapper, {
  type KuikaModalIconIntent
} from "../../ui-kit/kuika-modal-icon-wrapper/kuika-modal-icon-wrapper";
import KuikaH5 from "../../ui-kit/typography/H5/H5";
import "./prompt-dialog.scss";

interface PromptDialogProps {
  open: boolean;
  title: string;
  message?: string;
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  // Empty submitted value is allowed only when allowEmpty (e.g. an optional date); otherwise Save is
  // disabled until there's text.
  allowEmpty?: boolean;
  // Overrides the badge's meaning. Defaults to "create".
  intent?: KuikaModalIconIntent;
  // Overrides the badge's glyph. Defaults to "plus".
  iconName?: string;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}

// Single-field input dialog — the text-entry counterpart to ConfirmDialog, so board flows can ask for
// a name/URL/date through the themed modal instead of window.prompt. Driven imperatively via
// promptDialog() in confirm-service.
const PromptDialog: React.FC<PromptDialogProps> = ({
  open,
  title,
  message,
  label,
  placeholder,
  defaultValue = "",
  confirmLabel = "Save",
  cancelLabel = "Cancel",
  allowEmpty = false,
  intent = "create",
  iconName = "plus",
  onSubmit,
  onCancel
}) => {
  const [value, setValue] = useState<string>(defaultValue);
  // The answer is handed over only after the ✓ has been seen (handing it over closes the dialog), so
  // the commit has no loading phase — only the ✓, held long enough to be read.
  const [submitStatus, setSubmitStatus] = useState<KuikaButtonStatus>("idle");
  const submitMachineRef = useRef<SaveStatus | null>(null);
  if (submitMachineRef.current === null) {
    submitMachineRef.current = new SaveStatus(setSubmitStatus);
  }
  const submitMachine: SaveStatus = submitMachineRef.current;
  useEffect((): (() => void) => (): void => submitMachine.dispose(), [submitMachine]);

  // Seed (and re-seed) the field each time the dialog opens.
  useEffect(() => {
    if (open) setValue(defaultValue);
  }, [open, defaultValue]);

  // A new question (another prompt replacing this one, or a reopen) starts with a free button, and a
  // held answer to the previous question is dropped instead of being handed to the new one.
  useEffect((): void => {
    submitMachine.reset();
  }, [open, title, submitMachine]);

  const trimmed: string = value.trim();
  const canSubmit: boolean = allowEmpty || trimmed.length > 0;

  const submit = (): void => {
    if (!canSubmit) return;
    // A second press or Enter during the ✓ would answer twice.
    if (!submitMachine.canStart) return;
    submitMachine.succeed((): void => onSubmit(trimmed));
  };

  // Cancel, the X and Escape. During the ✓ hold the answer is already on its way; a cancel then
  // would close the dialog without it.
  const handleCancel = (): void => {
    if (submitMachine.current === "success") return;
    onCancel();
  };

  return (
    <KModal showModal={open} closeModal={handleCancel} centered width={420} header={title}>
      <div className="kuika-prompt">
        <div className="kuika-prompt__header">
          <KuikaModalIconWrapper iconName={iconName} intent={intent} />
          <div className="kuika-prompt__title">
            <KuikaH5>{title}</KuikaH5>
          </div>
        </div>
        {message && <div className="kuika-prompt__message">{message}</div>}
        {label && <span className="kuika-prompt__label">{label}</span>}
        <KuikaInput
          autoFocus
          size="md"
          value={value}
          placeholder={placeholder}
          onChange={(_event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, next: string | number) =>
            setValue(String(next))
          }
          onPressEnter={submit}
        />
        <div className="kuika-prompt__actions">
          <KuikaButton type="tertiary" color="neutral" onClick={handleCancel}>
            {cancelLabel}
          </KuikaButton>
          <KuikaButton type="primary" onClick={submit} disabled={!canSubmit} status={submitStatus}>
            {confirmLabel}
          </KuikaButton>
        </div>
      </div>
    </KModal>
  );
};

export default PromptDialog;
